'use memo';

import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState, useTransition } from 'react';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import NavigationCard, { type NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import { Text } from '@/components/Typography/Text';
import { orpc } from '@/lib/api/orpc';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { SettingsItemCard } from '../../../../../-components/SettingsItem';
import { MappingEditor } from './-components/MappingEditor';
import { MappingViewer } from './-components/MappingViewer';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/$id/_pipeline-detail/data-link-settings/')({
  component: OutboundFieldMappingsPage
});

/**
 * OUT FieldMapping 編集ページ。IN data-link-settings と同型の
 * 「左サイドバー（NavigationCard）+ 右に設定カード」レイアウト。
 * 左カラムの説明文は廃し、サイドバーで PEOPLE 種別セクションへジャンプする。
 */
function OutboundFieldMappingsPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline'
  });
  const {
    id: pipelineId
  } = Route.useParams();
  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  // 値変換は FieldMapping から独立した正規化リソース。編集時に集合置換へ渡すため別 query で取得する。
  const valueMappingsQuery = useQuery({
    ...orpc.outboundDataPipeline.listValueMappings.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isStartEditPending, startTransition] = useTransition();
  const startEdit = useCallback(() => {
    startTransition(() => setIsEditing(true));
  }, []);
  const finishEdit = useCallback(() => setIsEditing(false), []);
  const isPeople = detailQuery.data?.basicInfo?.dataType === 'PEOPLE';

  // IN と同じく当面は「マッピング設定」単一項目のみ（種別ジャンプは必要になれば追加）。
  const navigationSections: NavigationSectionGroup[] = [{
    titleLabel: t('detail.dataLinkSettings.mapping.title'),
    to: '/settings/data-connector/outbound/data-pipeline/$id/data-link-settings',
    hash: 'mapping-settings'
  }];
  return <FillScreen>
      <HStack className="gap-9 px-12 py-6">
        <div className="sticky top-6 w-52 shrink-0">
          <NavigationCard sections={navigationSections} />
        </div>

        <VStack className="min-w-0 flex-1 gap-6">
          <SettingsItemCard id="mapping-settings" className={cn('overflow-visible', (isEditing || isStartEditPending) && 'ring-2 ring-primary-fill')}>
            {detailQuery.data == null || isEditing && valueMappingsQuery.data == null ? <Text variant="p" className="text-neutral-muted-on-fill">
                {t('loading')}
              </Text> : isEditing ? <MappingEditor pipelineId={pipelineId} mappings={detailQuery.data.fieldMappings} valueMappings={valueMappingsQuery.data?.items ?? []} isPeople={isPeople} onDone={finishEdit} /> : <MappingViewer pipelineId={pipelineId} mappings={detailQuery.data.fieldMappings} isPeople={isPeople} isStartEditPending={isStartEditPending} onStartEdit={startEdit} />}
          </SettingsItemCard>
        </VStack>
      </HStack>
    </FillScreen>;
}